import React, { useMemo, useState } from "react";
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  TablePagination,
  TableSortLabel,
  Toolbar,
  Typography,
  Box,
  Chip,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Stack,
  IconButton,
  Tooltip,
  Collapse,
  Divider,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SendIcon from "@mui/icons-material/Send";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import LinkIcon from "@mui/icons-material/Link";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TimelineIcon from "@mui/icons-material/Timeline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type { EnhancedMessageTrackingData } from "../../services/outreach/types";

interface IndividualEmailTrackingTableProps {
  records: EnhancedMessageTrackingData[];
  onRefresh?: () => void;
  onViewDetails?: (record: EnhancedMessageTrackingData) => void;
  title?: string;
}

const statusOptions: Array<{ label: string; value: string | null }> = [
  { label: "All statuses", value: null },
  { label: "Sent", value: "sent" },
  { label: "Delivered", value: "delivered" },
  { label: "Opened", value: "opened" },
  { label: "Clicked", value: "clicked" },
  { label: "Failed", value: "failed" },
  { label: "Pending", value: "pending" },
];

const statusColors: Record<
  string,
  "default" | "success" | "error" | "warning" | "info"
> = {
  sent: "success",
  delivered: "success",
  opened: "info",
  clicked: "info",
  failed: "error",
  pending: "warning",
};

const columns = [
  { id: "recipient", label: "Recipient" },
  { id: "status", label: "Status" },
  { id: "messageId", label: "Message ID" },
  { id: "timestamp", label: "Last Event" },
  { id: "subject", label: "Subject" },
];

const formatDate = (date: string | undefined) =>
  date ? new Date(date).toLocaleString() : "--";

const toIsoString = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return parsed.toISOString();
};

const normalizeEventType = (type?: string) => (type ?? "").toLowerCase();

const capitalizeWords = (value: string) =>
  value
    .split(/[\s_\-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const EVENT_TYPE_LABELS: Record<string, string> = {
  send: "Sent",
  sent: "Sent",
  delivery: "Delivered",
  delivered: "Delivered",
  open: "Opened",
  opened: "Opened",
  click: "Clicked",
  clicked: "Clicked",
  bounce: "Bounced",
  bounced: "Bounced",
  complaint: "Complaint",
  complained: "Complaint",
  reject: "Rejected",
  rejected: "Rejected",
  failure: "Failure",
  failed: "Failure",
  unsubscribe: "Unsubscribed",
  unsubscribed: "Unsubscribed",
};

const EVENT_TYPE_VISUALS: Record<
  string,
  { icon: React.ElementType; color: string }
> = {
  send: { icon: SendIcon, color: "primary.main" },
  sent: { icon: SendIcon, color: "primary.main" },
  delivery: { icon: MarkEmailReadIcon, color: "success.main" },
  delivered: { icon: MarkEmailReadIcon, color: "success.main" },
  open: { icon: VisibilityIcon, color: "info.main" },
  opened: { icon: VisibilityIcon, color: "info.main" },
  click: { icon: LinkIcon, color: "secondary.main" },
  clicked: { icon: LinkIcon, color: "secondary.main" },
  bounce: { icon: ReportProblemIcon, color: "error.main" },
  bounced: { icon: ReportProblemIcon, color: "error.main" },
  complaint: { icon: ReportProblemIcon, color: "warning.main" },
  complained: { icon: ReportProblemIcon, color: "warning.main" },
  failure: { icon: ErrorOutlineIcon, color: "error.main" },
  failed: { icon: ErrorOutlineIcon, color: "error.main" },
  reject: { icon: ErrorOutlineIcon, color: "error.main" },
  rejected: { icon: ErrorOutlineIcon, color: "error.main" },
  unsubscribe: { icon: ScheduleIcon, color: "warning.main" },
  unsubscribed: { icon: ScheduleIcon, color: "warning.main" },
};

const DEFAULT_EVENT_VISUAL = {
  icon: TimelineIcon as React.ElementType,
  color: "rgba(255,255,255,0.7)",
};

type TimelineEntry = {
  key: string;
  type: string;
  label: string;
  timestamp: string;
  detail?: Record<string, any>;
};

type EnhancedTrackingEvent = NonNullable<
  EnhancedMessageTrackingData["events"]
>[number];

type RecordMetadata = {
  messageId: string;
  providerLabel: string;
  timeline: TimelineEntry[];
  diagnosticCodes: string[];
  complaintFeedback?: string;
  bounceType?: string;
  bounceSubType?: string;
  smtpResponse?: string;
  batchId?: string;
};

const getEventVisual = (type: string) => {
  const normalized = normalizeEventType(type);
  const visual = EVENT_TYPE_VISUALS[normalized];
  const Icon = (visual?.icon ?? DEFAULT_EVENT_VISUAL.icon) as React.ElementType;
  return {
    Icon,
    color: visual?.color ?? DEFAULT_EVENT_VISUAL.color,
  };
};

const humanizeType = (type: string) => {
  const normalized = normalizeEventType(type);
  if (!normalized) {
    return "Event";
  }
  return EVENT_TYPE_LABELS[normalized] ?? capitalizeWords(normalized);
};

const describeEventDetail = (
  type: string,
  detail?: Record<string, any>
): string[] => {
  if (!detail) {
    return [];
  }

  const normalized = normalizeEventType(type);

  if (normalized === "bounce" || normalized === "bounced") {
    const parts: string[] = [];
    if (detail.bounceType) {
      parts.push(`Type: ${detail.bounceType}`);
    }
    if (detail.bounceSubType) {
      parts.push(`Subtype: ${detail.bounceSubType}`);
    }
    if (detail.smtpResponse) {
      parts.push(`SMTP: ${detail.smtpResponse}`);
    }
    if (Array.isArray(detail.bouncedRecipients)) {
      detail.bouncedRecipients.forEach((recipient: any, index: number) => {
        if (recipient?.diagnosticCode) {
          parts.push(`Diag ${index + 1}: ${recipient.diagnosticCode}`);
        }
      });
    } else if (detail.diagnosticCode) {
      parts.push(`Diag: ${detail.diagnosticCode}`);
    }
    return parts;
  }

  if (normalized === "complaint" || normalized === "complained") {
    const parts: string[] = [];
    if (detail.complaintFeedbackType) {
      parts.push(`Feedback: ${detail.complaintFeedbackType}`);
    }
    if (detail.feedbackId) {
      parts.push(`Feedback ID: ${detail.feedbackId}`);
    }
    return parts;
  }

  if (normalized === "click" || normalized === "clicked") {
    const link =
      detail.link ??
      detail.linkTarget ??
      detail.url ??
      detail.linkUrl ??
      detail.destination;
    const parts: string[] = [];
    if (link) {
      parts.push(`Link: ${link}`);
    }
    if (detail.userAgent) {
      parts.push(`Agent: ${detail.userAgent}`);
    }
    return parts;
  }

  if (normalized === "open" || normalized === "opened") {
    const parts: string[] = [];
    if (detail.ipAddress) {
      parts.push(`IP: ${detail.ipAddress}`);
    }
    if (detail.userAgent) {
      parts.push(`Agent: ${detail.userAgent}`);
    }
    return parts;
  }

  if (detail.error) {
    return [`Error: ${detail.error}`];
  }

  return [];
};

const buildTimelineEntries = (
  record: EnhancedMessageTrackingData,
  events: EnhancedTrackingEvent[],
  diagnosticCodes: string[]
): TimelineEntry[] => {
  const entries: TimelineEntry[] = [];

  events.forEach((event, index) => {
    const timestampIso = toIsoString(event.timestamp);
    if (!timestampIso) {
      return;
    }
    const type = normalizeEventType(event.type);
    entries.push({
      key: `${record.id}-event-${index}`,
      type,
      label: humanizeType(type),
      timestamp: timestampIso,
      detail: event.detail,
    });
  });

  const summaryCandidates: Array<{
    type: string;
    timestamp?: string;
    detail?: Record<string, any>;
  }> = [
    { type: "sent", timestamp: record.timestamp },
    { type: "delivered", timestamp: record.deliveredAt },
    { type: "opened", timestamp: record.openedAt },
    { type: "clicked", timestamp: record.clickedAt },
    {
      type: "bounce",
      timestamp: record.bouncedAt,
      detail: diagnosticCodes.length
        ? { diagnosticCodes }
        : undefined,
    },
    { type: "complaint", timestamp: record.complainedAt },
    { type: "unsubscribe", timestamp: record.unsubscribedAt },
  ];

  summaryCandidates.forEach((candidate, index) => {
    const timestampIso = toIsoString(candidate.timestamp);
    if (!timestampIso) {
      return;
    }
    const type = normalizeEventType(candidate.type);
    const key = `${record.id}-summary-${type}-${index}`;
    if (
      !entries.some(
        (entry) => entry.timestamp === timestampIso && entry.type === type
      )
    ) {
      entries.push({
        key,
        type,
        label: humanizeType(type),
        timestamp: timestampIso,
        detail: candidate.detail,
      });
    }
  });

  return entries.sort(
    (a, b) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
};

const collectRecordMetadata = (
  record: EnhancedMessageTrackingData
): RecordMetadata => {
  const events: EnhancedTrackingEvent[] = Array.isArray(record.events)
    ? record.events
    : [];
  const sesDetail = record.sesEventDetail as Record<string, any> | undefined;

  const detailCandidates = [
    sesDetail,
    ...events
      .map((event) => event.detail)
      .filter((candidate): candidate is Record<string, any> => Boolean(candidate)),
  ];

  let messageId = record.mailgunMessageId;
  if (!messageId) {
    messageId =
      detailCandidates
        .map((detail) => detail?.mail?.messageId)
        .find(
          (candidate): candidate is string =>
            typeof candidate === "string" && candidate.length > 0
        ) ??
      events
        .map((event) => event.messageId)
        .find(
          (candidate): candidate is string =>
            typeof candidate === "string" && candidate.length > 0
        ) ??
      record.id;
  }

  const diagnosticSet = new Set<string>();
  const bounceDetails: Record<string, any>[] = [];

  detailCandidates.forEach((detail) => {
    if (detail?.bounce) {
      bounceDetails.push(detail.bounce);
    }
    if (detail?.bouncedRecipients && Array.isArray(detail.bouncedRecipients)) {
      bounceDetails.push(detail);
    }
  });

  events.forEach((event) => {
    const normalized = normalizeEventType(event.type);
    if (
      (normalized === "bounce" || normalized === "bounced") &&
      event.detail
    ) {
      bounceDetails.push(event.detail);
    }
    if (
      (normalized === "failure" ||
        normalized === "failed" ||
        normalized === "reject" ||
        normalized === "rejected") &&
      event.detail?.error
    ) {
      diagnosticSet.add(event.detail.error as string);
    }
  });

  bounceDetails.forEach((detail) => {
    if (typeof detail?.diagnosticCode === "string") {
      diagnosticSet.add(detail.diagnosticCode);
    }
    if (typeof detail?.smtpResponse === "string") {
      diagnosticSet.add(detail.smtpResponse);
    }
    const bouncedRecipients = detail?.bouncedRecipients;
    if (Array.isArray(bouncedRecipients)) {
      bouncedRecipients.forEach((recipient) => {
        if (recipient?.diagnosticCode) {
          diagnosticSet.add(recipient.diagnosticCode);
        }
      });
    }
  });

  if (record.error) {
    diagnosticSet.add(record.error);
  }

  const complaintEventDetail = events
    .filter((event) => {
      const normalized = normalizeEventType(event.type);
      return normalized === "complaint" || normalized === "complained";
    })
    .map((event) => event.detail)
    .find((detail): detail is Record<string, any> => Boolean(detail));

  const complaintDetail =
    (sesDetail?.complaint as Record<string, any> | undefined) ??
    complaintEventDetail;

  const diagnosticCodes = Array.from(diagnosticSet).filter(Boolean);

  const bounceDetail =
    (sesDetail?.bounce as Record<string, any> | undefined) ??
    bounceDetails.find(Boolean);

  const providerLabel =
    record.provider?.toString().toUpperCase() ??
    (sesDetail ? "SES" : "MAILGUN");

  const timeline = buildTimelineEntries(record, events, diagnosticCodes);

  return {
    messageId,
    providerLabel,
    timeline,
    diagnosticCodes,
    complaintFeedback:
      (complaintDetail?.complaintFeedbackType as string | undefined) ??
      (complaintDetail?.feedbackId as string | undefined),
    bounceType: bounceDetail?.bounceType,
    bounceSubType: bounceDetail?.bounceSubType,
    smtpResponse: bounceDetail?.smtpResponse,
    batchId: (record as { batchId?: string }).batchId,
  };
};

export const IndividualEmailTrackingTable: React.FC<
  IndividualEmailTrackingTableProps
> = ({ records, onRefresh, onViewDetails }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [orderBy, setOrderBy] =
    useState<keyof EnhancedMessageTrackingData>("timestamp");
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">("desc");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const handleSort = (property: keyof EnhancedMessageTrackingData) => {
    const isAsc = orderBy === property && orderDirection === "asc";
    setOrderBy(property);
    setOrderDirection(isAsc ? "desc" : "asc");
  };

  const decoratedRecords = useMemo(
    () =>
      records.map((record) => ({
        record,
        metadata: collectRecordMetadata(record),
      })),
    [records]
  );

  const filteredRecords = useMemo(() => {
    return decoratedRecords
      .filter(({ record, metadata }) => {
        if (statusFilter && record.status !== statusFilter) {
          return false;
        }
        if (!searchTerm) {
          return true;
        }
        const needle = searchTerm.toLowerCase();
        return (
          record.recipient.toLowerCase().includes(needle) ||
          record.subject.toLowerCase().includes(needle) ||
          metadata.messageId.toLowerCase().includes(needle) ||
          metadata.providerLabel.toLowerCase().includes(needle) ||
          metadata.diagnosticCodes.some((code) =>
            code.toLowerCase().includes(needle)
          ) ||
          (record.events ?? []).some((event) =>
            (event.type ?? "").toLowerCase().includes(needle)
          )
        );
      })
      .sort((a, b) => {
        const aValue = a.record[orderBy];
        const bValue = b.record[orderBy];
        if (aValue === undefined || bValue === undefined) {
          return 0;
        }
        if (orderBy === "timestamp") {
          const aDate = new Date(aValue as string).getTime();
          const bDate = new Date(bValue as string).getTime();
          return orderDirection === "asc" ? aDate - bDate : bDate - aDate;
        }
        if (typeof aValue === "string" && typeof bValue === "string") {
          return orderDirection === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        return 0;
      });
  }, [
    decoratedRecords,
    statusFilter,
    searchTerm,
    orderBy,
    orderDirection,
  ]);

  const paginatedRecords = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRecords.slice(start, start + rowsPerPage);
  }, [filteredRecords, page, rowsPerPage]);

  const toggleRowExpansion = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper
      sx={{
        mt: 3,
        background:
          "linear-gradient(135deg, rgba(24,24,24,0.95), rgba(12,60,64,0.9))",
        color: "#fff",
        border: "1px solid rgba(65, 200, 196, 0.2)",
      }}
    >
      <Toolbar sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <Box flexGrow={1}></Box>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter ?? "all"}
              label="Status"
              onChange={(event) => {
                const value = event.target.value;
                setStatusFilter(value === "all" ? null : (value as string));
                setPage(0);
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.label} value={option.value ?? "all"}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Search recipients"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 220 }}
          />
          {onRefresh && (
            <Tooltip title="Refresh tracking data">
              <IconButton color="primary" onClick={onRefresh}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Toolbar>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                padding="checkbox"
                sx={{ color: "rgba(255,255,255,0.8)" }}
              />
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{ color: "rgba(255,255,255,0.8)" }}
                >
                  {column.id === "timestamp" ? (
                    <TableSortLabel
                      active={orderBy === "timestamp"}
                      direction={orderDirection}
                      onClick={() => handleSort("timestamp")}
                      sx={{
                        color: "inherit",
                        "&.MuiTableSortLabel-root": { color: "inherit" },
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              <TableCell sx={{ color: "rgba(255,255,255,0.8)" }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRecords.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 2}>
                  <Box
                    sx={{
                      py: 3,
                      textAlign: "center",
                      color: "rgba(255,255,255,0.6)",
                    }}
                  >
                    No tracking events match your filters yet.
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedRecords.map(({ record, metadata }) => {
                const isExpanded = expandedRows.has(record.id);
                return (
                  <React.Fragment key={`${record.id}-${record.timestamp}`}>
                    <TableRow hover>
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpansion(record.id)}
                          aria-label={
                            isExpanded ? "Collapse details" : "Expand details"
                          }
                          sx={{ color: "rgba(255,255,255,0.7)" }}
                        >
                          {isExpanded ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ color: "#fff", fontWeight: 500 }}>
                        {record.recipient}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={record.status}
                          color={statusColors[record.status] ?? "default"}
                          size="small"
                          sx={{ textTransform: "capitalize", fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: "rgba(255,255,255,0.8)" }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {metadata.messageId || "--"}
                          </Typography>
                          <Chip
                            label={metadata.providerLabel}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: "rgba(255,255,255,0.4)",
                              color: "rgba(255,255,255,0.8)",
                              "& .MuiChip-label": {
                                px: 0.75,
                              },
                            }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ color: "rgba(255,255,255,0.8)" }}>
                        {formatDate(record.timestamp)}
                      </TableCell>
                      <TableCell sx={{ color: "rgba(255,255,255,0.8)" }}>
                        {record.subject}
                      </TableCell>
                      <TableCell align="right">
                        {onViewDetails && (
                          <Tooltip title="View timeline">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => onViewDetails(record)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={columns.length + 2}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              px: 3,
                              py: 2,
                              backgroundColor: "rgba(255,255,255,0.04)",
                              borderTop: "1px solid rgba(255,255,255,0.08)",
                            }}
                          >
                            <Stack spacing={2}>
                              <Stack
                                direction={{ xs: "column", md: "row" }}
                                spacing={2}
                                alignItems={{ xs: "flex-start", md: "center" }}
                              >
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "rgba(255,255,255,0.65)" }}
                                  >
                                    Message ID
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontFamily: "monospace" }}
                                  >
                                    {metadata.messageId}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "rgba(255,255,255,0.65)" }}
                                  >
                                    Provider
                                  </Typography>
                                  <Chip
                                    label={metadata.providerLabel}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    sx={{
                                      borderColor: "rgba(65,200,196,0.5)",
                                      color: "rgba(200,255,255,0.9)",
                                    }}
                                  />
                                </Box>
                                {metadata.batchId && (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "rgba(255,255,255,0.65)" }}
                                    >
                                      Batch
                                    </Typography>
                                    <Typography variant="body2">
                                      {metadata.batchId}
                                    </Typography>
                                  </Box>
                                )}
                              </Stack>

                              <Divider
                                sx={{ borderColor: "rgba(255,255,255,0.08)" }}
                              />

                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ color: "#fff", mb: 1 }}
                                >
                                  Event Timeline
                                </Typography>
                                {metadata.timeline.length === 0 ? (
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "rgba(255,255,255,0.6)" }}
                                  >
                                    No event history available yet.
                                  </Typography>
                                ) : (
                                  <Stack spacing={1.5}>
                                    {metadata.timeline.map((event) => {
                                      const { Icon, color } = getEventVisual(
                                        event.type
                                      );
                                      const detailNotes = describeEventDetail(
                                        event.type,
                                        event.detail
                                      );
                                      return (
                                        <Stack
                                          key={event.key}
                                          direction="row"
                                          spacing={1.5}
                                          alignItems="flex-start"
                                        >
                                          <Box
                                            sx={{
                                              width: 32,
                                              height: 32,
                                              borderRadius: "50%",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              backgroundColor:
                                                "rgba(255,255,255,0.08)",
                                              color,
                                              flexShrink: 0,
                                            }}
                                          >
                                            <Icon fontSize="small" />
                                          </Box>
                                          <Box>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontWeight: 600,
                                                color,
                                              }}
                                            >
                                              {event.label}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                display: "block",
                                                color:
                                                  "rgba(255,255,255,0.7)",
                                              }}
                                            >
                                              {formatDate(event.timestamp)}
                                            </Typography>
                                            {detailNotes.map(
                                              (note, detailIndex) => (
                                                <Typography
                                                  key={`${event.key}-detail-${detailIndex}`}
                                                  variant="caption"
                                                  sx={{
                                                    display: "block",
                                                    color:
                                                      "rgba(255,255,255,0.7)",
                                                  }}
                                                >
                                                  {note}
                                                </Typography>
                                              )
                                            )}
                                          </Box>
                                        </Stack>
                                      );
                                    })}
                                  </Stack>
                                )}
                              </Box>

                              {metadata.diagnosticCodes.length > 0 && (
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "#fff",
                                      mb: 1,
                                    }}
                                  >
                                    Diagnostic Codes
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    flexWrap="wrap"
                                  >
                                    {metadata.diagnosticCodes.map((code) => (
                                      <Chip
                                        key={`${record.id}-diag-${code}`}
                                        label={code}
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                      />
                                    ))}
                                  </Stack>
                                </Box>
                              )}

                              {(metadata.bounceType ||
                                metadata.bounceSubType ||
                                metadata.smtpResponse) && (
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ color: "#fff", mb: 1 }}
                                  >
                                    Bounce Details
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    flexWrap="wrap"
                                  >
                                    {metadata.bounceType && (
                                      <Chip
                                        label={`Type: ${metadata.bounceType}`}
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                      />
                                    )}
                                    {metadata.bounceSubType && (
                                      <Chip
                                        label={`Subtype: ${metadata.bounceSubType}`}
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                      />
                                    )}
                                    {metadata.smtpResponse && (
                                      <Chip
                                        label={metadata.smtpResponse}
                                        size="small"
                                        variant="outlined"
                                        sx={{ color: "rgba(255,255,255,0.8)" }}
                                      />
                                    )}
                                  </Stack>
                                </Box>
                              )}

                              {metadata.complaintFeedback && (
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ color: "#fff", mb: 1 }}
                                  >
                                    Complaint Feedback
                                  </Typography>
                                  <Chip
                                    label={metadata.complaintFeedback}
                                    size="small"
                                    color="warning"
                                  />
                                </Box>
                              )}
                            </Stack>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredRecords.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </Paper>
  );
};

export default IndividualEmailTrackingTable;
